import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isGuest: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isGuest: false,
  signOut: async () => {},
});

/**
 * Guest Preview mode: when running on Lovable preview hosts (or localhost),
 * we automatically create an anonymous Supabase session so the user lands
 * directly on the app instead of /auth. In production (custom domain or
 * .lovable.app published site without "preview"/"id-preview" in hostname)
 * normal auth applies.
 */
function isPreviewHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.includes("id-preview--") ||
    h.includes("preview--") ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".sandbox.lovable.dev")
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // CRITICAL: subscribe BEFORE getSession to avoid race
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      setIsGuest(!!sess?.user?.is_anonymous);
      if (sess?.user) {
        setTimeout(() => checkAdmin(sess.user.id), 0);
      } else {
        setIsAdmin(false);
      }
    });

    (async () => {
      const { data: { session: sess } } = await supabase.auth.getSession();
      if (cancelled) return;

      if (sess?.user) {
        setSession(sess);
        setUser(sess.user);
        setIsGuest(!!sess.user.is_anonymous);
        checkAdmin(sess.user.id);
        setLoading(false);
        return;
      }

      // No session → on preview hosts, auto-create anonymous session
      if (isPreviewHost()) {
        try {
          const { data, error } = await supabase.auth.signInAnonymously();
          if (cancelled) return;
          if (error) {
            console.warn("[Guest Preview] anonymous sign-in failed:", error.message);
          } else if (data.session) {
            setSession(data.session);
            setUser(data.user);
            setIsGuest(true);
          }
        } catch (e) {
          console.warn("[Guest Preview] anonymous sign-in threw:", e);
        }
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function checkAdmin(uid: string) {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!data);
  }

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setIsAdmin(false);
    setIsGuest(false);
  };

  return (
    <Ctx.Provider value={{ user, session, loading, isAdmin, isGuest, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
